import React, { useContext } from 'react';
import {
  Banner,
  Body,
  Button,
  Group,
  H7,
  Icon,
  Stack,
} from '@qred/components-library';
import useTranslate from '~/hooks/useTranslate';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '~/hooks/useAppDispatch';
import { RootState } from '~/store';
import { updateForm } from '~/store/slices/loanOffer.slice';
import useModals from '~/hooks/useModals';
import { pushToGtmLoanAction } from '~/store/actions/gtmActions';
import { useLoanOfferSelectors } from '~/store/selectors/loanOffer.selector';
import useMarketConfig from '~/hooks/useMarketConfig';
import OwnersList from './OwnersList';
import OwnerManagementModal from './OwnerManagementModal';
import { ValidationContext } from '~/components/hoc/withValidation';

const OwnershipAdditionalOwnersPanel = () => {
  const dispatch = useAppDispatch();
  const {
    loanOffer: {
      form: { companyHasOtherBeneficialOwners, owners },
    },
    matchedMedia: { mobile },
  } = useSelector((state: RootState) => state);
  const validationContext = useContext(ValidationContext);

  const t = useTranslate();
  const modals = useModals();
  const {
    applicantHasAnsweredBeneficialOwnersQuestion,
    totalAmountOfShares,
    applicantIsABeneficialOwner,
    alternativeOwnerManuallyAdded,
  } = useLoanOfferSelectors();

  const { marketHasPersonalNumber } = useMarketConfig();

  const onChange = (otherBeneficialOwnersExist: boolean) => {
    dispatch(
      updateForm({
        companyHasOtherBeneficialOwners: otherBeneficialOwnersExist,
      })
    );
  };

  const openOwnerManagementModal = () => {
    dispatch(
      pushToGtmLoanAction({
        actionName: 'loan_offer_add_owner',
      })
    );

    modals.openModal({
      header: <H7>{t('OnboardingOffer.AddNewOwner')}</H7>,
      children: (
        <OwnerManagementModal
          isAlternativeBeneficialOwner={!companyHasOtherBeneficialOwners}
          askForPersonalNumber={marketHasPersonalNumber}
          askForDateOfBirth={!marketHasPersonalNumber}
        />
      ),
      size: 'md',
      fullScreen: mobile,
      filledBackground: true,
      hasOwnFooter: true,
    });
  };

  const applicantCanAddAdditionalBeneficialOwners =
    companyHasOtherBeneficialOwners && totalAmountOfShares < 75;

  const applicantCanAddAlternativeOwners =
    !companyHasOtherBeneficialOwners && !applicantIsABeneficialOwner;

  return (
    <Stack>
      <Body size="lg">
        {t('OnboardingOffer.AdditionalOwnersPanelDescription')}
      </Body>
      <Group grow justify="space-between">
        <Button
          onClick={() => onChange(true)}
          variant="secondary"
          data-active={companyHasOtherBeneficialOwners}
        >
          {t('Yes')}
        </Button>
        <Button
          onClick={() => onChange(false)}
          variant="secondary"
          data-active={
            applicantHasAnsweredBeneficialOwnersQuestion &&
            !companyHasOtherBeneficialOwners
          }
        >
          {t('No')}
        </Button>
      </Group>
      {applicantHasAnsweredBeneficialOwnersQuestion && (
        <>
          {applicantCanAddAdditionalBeneficialOwners && (
            <Body size="lg">
              {t('OnboardingOffer.AdditionalOwnersPanelAddOwnerLabel')}
            </Body>
          )}

          {applicantCanAddAlternativeOwners && (
            <Body size="lg">
              {t(
                'OnboardingOffer.AdditionalOwnersPanelAddAlternativeBeneficialOwner'
              )}
            </Body>
          )}

          {!!owners.length && <OwnersList />}

          <Banner
            state="critical"
            withCloseButton={false}
            message={t('OnboardingOffer.TotalOwnershipSharesMoreThan100')}
            opened={
              validationContext.validationErrors.totalOwnershipSharesMoreThan100
            }
          />

          {(applicantCanAddAdditionalBeneficialOwners ||
            applicantCanAddAlternativeOwners) && (
            <Stack align="center">
              <Button
                onClick={openOwnerManagementModal}
                variant="secondary"
                fullWidth={mobile}
                disabled={alternativeOwnerManuallyAdded}
              >
                <Icon src="Plus" mr="0.5em" />
                {t('OnboardingOffer.AdditionalOwnersPanelAddOwnerButtonLabel')}
              </Button>
            </Stack>
          )}
        </>
      )}
    </Stack>
  );
};

export default OwnershipAdditionalOwnersPanel;
